import React, { useContext } from 'react'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getPaymentDueDate, isOfferDefaultDepositAmountPercentage } from 'lib/depositsUtils'
import useToggle from 'hooks/useToggle'
import { DepositInfoModal } from 'components/Common/DepositInfo/DepositInfoModal'
import { getDepositAmountI18NKeyDetails } from 'lib/TourV2/depositAmountStringBuilder'

import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { useTranslation } from 'react-i18next'

const Container = styled.div`
  background-color: ${props => props.theme.palette.highlight.primary.light};
  border-radius: ${props => props.theme.borderRadius.large};
  display: flex;
  padding: ${rem(16)};
`

const RightSide = styled.div`
  margin-left: ${rem(16)};

  ${mediaQueryUp.sm} {
    display: flex;
    align-items: baseline;
  }
`

interface Props {
  offerType: App.OfferType;
  depositAmountPercentage: number
  depositThresholds?: App.DepositThreshold
  depositType?: Tours.V2OfferDepositType
  depositAmount?: number;
}

export function SecureWithDepositBanner({ offerType, depositAmountPercentage, depositThresholds, depositType, depositAmount }: Props) {
  const paymentDueDaysBeforeCheckin = getPaymentDueDate(depositThresholds, offerType)
  const [isPolicyOpen, , openPolicy, closePolicy] = useToggle()

  const { t } = useTranslation(['common', 'deposit'])

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const { currentCurrency } = useContext(GeoContext)

  const depositAmountKeyDetails: ReturnType<typeof getDepositAmountI18NKeyDetails> =
    isOfferUsingDefaultDepositPercentage ? { type: 'unspecified', options: {} } :
        !depositType ? { type: 'percentage', options: { amount: depositAmountPercentage } } :
          getDepositAmountI18NKeyDetails(currentCurrency, defaultDepositAmountPercentage, depositType, depositAmountPercentage)

  return (
    <>
      <Container>
        <LineClockDollarIcon colour="primary" />
        <RightSide>
          <BodyText variant="large">
            {t('secureWithDepositLabel', { ns: 'deposit', context: depositAmountKeyDetails.type, ...depositAmountKeyDetails.options, numberOfDays: paymentDueDaysBeforeCheckin })}
          </BodyText>
          <TextLink onClick={openPolicy} size="large">{t('Find out more', { ns: 'common' })}</TextLink>
        </RightSide>
      </Container>
      <DepositInfoModal
        isModalOpen={isPolicyOpen}
        offerType={offerType}
        onCloseModal={closePolicy}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={depositThresholds}
        depositType={depositType}
        depositAmount={depositAmount}
      />
    </>
  )
}
